import { useJsApiLoader, type Libraries } from '@react-google-maps/api';

/**
 * Singleton loader for the Google Maps JavaScript API.
 *
 * Uses a fixed `id` so every consumer in the app (Places autocomplete, the
 * mobile map engine, the Street View modal, etc.) shares a single <script>
 * tag and a single billed session. Adding a new library here will work as
 * long as it is loaded BEFORE google.maps is first used (the loader keeps
 * track of the union of libraries requested across calls).
 */
const GOOGLE_MAPS_SCRIPT_ID = 'dendromap-google-maps';

const DEFAULT_LIBRARIES: Libraries = ['places', 'geometry', 'marker'];

export interface UseGoogleMapsScriptOptions {
  apiKey: string;
  libraries?: Libraries;
  language?: string;
  region?: string;
}

export interface UseGoogleMapsScriptResult {
  isLoaded: boolean;
  loadError: Error | undefined;
}

export function useGoogleMapsScript({
  apiKey,
  libraries = DEFAULT_LIBRARIES,
  language = 'fr',
  region = 'FR',
}: UseGoogleMapsScriptOptions): UseGoogleMapsScriptResult {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey || '',
    libraries,
    language,
    region,
  });

  return {
    isLoaded: Boolean(apiKey) && isLoaded,
    loadError,
  };
}

export { GOOGLE_MAPS_SCRIPT_ID, DEFAULT_LIBRARIES };
